/**
 * MA-ADMIN-013: Consent Version Catalog Component
 * 
 * Displays the complete catalog of consent versions for a funnel.
 * 
 * Features:
 * - Active version display card
 * - Version history table
 * - Version detail modal (read-only)
 * - Usage tracking (leads per version)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Shield,
  ShieldCheck,
  Clock,
  Users,
  Eye,
  Check,
  Archive,
  FileText,
  Loader2,
  AlertCircle,
  RefreshCw,
  ChevronRight,
  Info,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

// =============================================================================
// Helper Functions
// =============================================================================

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatDateTime(dateStr) {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// =============================================================================
// Active Version Card
// =============================================================================

function ActiveVersionCard({ version, onViewDetails }) {
  if (!version) {
    return (
      <Card className="bg-slate-900/50 border-slate-800">
        <CardContent className="py-6">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-slate-800">
              <Shield className="h-5 w-5 text-slate-500" />
            </div>
            <div>
              <p className="text-slate-100 font-medium">No Active Version</p>
              <p className="text-sm text-slate-500">
                A consent version will be created on first save
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-gradient-to-r from-emerald-500/10 to-transparent border-emerald-500/30">
      <CardContent className="py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-500/20">
              <ShieldCheck className="h-5 w-5 text-emerald-400" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <p className="text-slate-100 font-semibold">{version.version_label}</p>
                <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30">
                  Active
                </Badge>
              </div>
              <div className="flex items-center gap-4 text-sm text-slate-400 mt-1">
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {formatDate(version.created_at)}
                </span>
                <span className="flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  {version.usage_count || 0} leads
                </span>
              </div>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onViewDetails(version)}
            className="border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10"
            data-testid="active-version-view-btn"
          >
            <Eye className="h-4 w-4 mr-1" />
            View
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// =============================================================================
// Version Detail Modal
// =============================================================================

function VersionDetailModal({ version, open, onClose }) {
  if (!version) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="text-slate-100 flex items-center gap-2">
            <FileText className="h-5 w-5 text-indigo-400" />
            {version.version_label}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Version ID: <code className="text-xs bg-slate-800 px-1.5 py-0.5 rounded">{version.id}</code>
          </DialogDescription>
        </DialogHeader>
        
        <ScrollArea className="max-h-[60vh]">
          <div className="space-y-6 pr-4">
            {/* Status & Metadata */}
            <div className="flex flex-wrap gap-4 pb-4 border-b border-slate-800">
              <div>
                <Label className="text-xs text-slate-500">Status</Label>
                <Badge className={cn(
                  "mt-1",
                  version.is_active 
                    ? "bg-emerald-500/20 text-emerald-300" 
                    : "bg-slate-700 text-slate-400"
                )}>
                  {version.is_active ? "Active" : "Archived"}
                </Badge>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Created</Label>
                <p className="text-sm text-slate-200 mt-1">{formatDateTime(version.created_at)}</p>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Usage</Label>
                <p className="text-sm text-slate-200 mt-1">{version.usage_count || 0} leads</p>
              </div>
            </div>
            
            {/* Consent Text */}
            <div>
              <Label className="text-xs text-slate-500 uppercase tracking-wider">Consent Language</Label>
              <div className="mt-2 p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                <p className="text-sm text-slate-200 whitespace-pre-wrap leading-relaxed">
                  {version.consent_text || '(No consent text)'}
                </p>
              </div>
            </div>
            
            {/* Compliance Flags */}
            <div>
              <Label className="text-xs text-slate-500 uppercase tracking-wider">Compliance Settings</Label>
              <div className="mt-2 grid grid-cols-2 gap-3">
                <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                  <p className="text-xs text-slate-400">Require Consent Step</p>
                  <div className="flex items-center gap-2 mt-1">
                    {version.require_consent_step ? (
                      <>
                        <Check className="h-4 w-4 text-emerald-400" />
                        <span className="text-sm text-emerald-300">Required</span>
                      </>
                    ) : (
                      <>
                        <AlertCircle className="h-4 w-4 text-amber-400" />
                        <span className="text-sm text-amber-300">Not Required</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                  <p className="text-xs text-slate-400">TrustedForm Mode</p>
                  <p className="text-sm text-slate-200 mt-1 capitalize">
                    {version.trustedform_mode === 'off' ? 'Disabled' : 
                     version.trustedform_mode === 'capture_only' ? 'Capture Only' :
                     version.trustedform_mode === 'enforce' ? 'Enforced' :
                     version.trustedform_mode || 'Off'}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Usage Timeline */}
            {(version.first_used_at || version.last_used_at) && (
              <div>
                <Label className="text-xs text-slate-500 uppercase tracking-wider">Usage Timeline</Label>
                <div className="mt-2 flex gap-4 text-sm">
                  {version.first_used_at && (
                    <div>
                      <span className="text-slate-400">First used: </span>
                      <span className="text-slate-200">{formatDateTime(version.first_used_at)}</span>
                    </div>
                  )}
                  {version.last_used_at && (
                    <div>
                      <span className="text-slate-400">Last used: </span>
                      <span className="text-slate-200">{formatDateTime(version.last_used_at)}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Immutability Notice */}
            <div className="flex items-start gap-2 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
              <Info className="h-4 w-4 text-amber-400 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-300/90">
                Consent versions are immutable and cannot be edited or deleted. 
                This ensures compliance audit trails remain intact.
              </p>
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Version Catalog Table
// =============================================================================

function VersionCatalogTable({ versions, onViewDetails }) {
  if (!versions || versions.length === 0) {
    return (
      <div className="text-center py-8 text-slate-500">
        <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p>No consent versions yet</p>
        <p className="text-sm mt-1">Versions will appear here after saving compliance settings</p>
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow className="border-slate-800 hover:bg-transparent">
          <TableHead className="text-slate-400">Version</TableHead>
          <TableHead className="text-slate-400">Status</TableHead>
          <TableHead className="text-slate-400">Created</TableHead>
          <TableHead className="text-slate-400">Usage</TableHead>
          <TableHead className="text-slate-400 text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {versions.map((version) => (
          <TableRow 
            key={version.id} 
            className="border-slate-800/50 hover:bg-slate-800/30"
          >
            <TableCell>
              <div className="flex items-center gap-2">
                {version.is_active ? (
                  <ShieldCheck className="h-4 w-4 text-emerald-400" />
                ) : (
                  <Archive className="h-4 w-4 text-slate-500" />
                )}
                <span className="text-slate-200 font-medium">{version.version_label}</span>
              </div>
            </TableCell>
            <TableCell>
              <Badge className={cn(
                "text-xs",
                version.is_active 
                  ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" 
                  : "bg-slate-700/50 text-slate-400 border-slate-600"
              )}>
                {version.is_active ? "Active" : "Archived"}
              </Badge>
            </TableCell>
            <TableCell className="text-slate-400 text-sm">
              {formatDate(version.created_at)}
            </TableCell>
            <TableCell className="text-slate-400 text-sm">
              {version.usage_count || 0} leads
            </TableCell>
            <TableCell className="text-right">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onViewDetails(version)}
                className="h-7 px-2 bg-indigo-500/10 border-indigo-500/30 text-indigo-300 hover:bg-indigo-500/20"
                data-testid={`view-version-${version.version_number}`}
              >
                <Eye className="h-3 w-3 mr-1" />
                View
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export default function ConsentVersionCatalog({ funnelId }) {
  const [versions, setVersions] = useState([]);
  const [activeVersion, setActiveVersion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Fetch versions
  const fetchVersions = useCallback(async () => {
    if (!funnelId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get(`/funnels/${funnelId}/consent-versions`);
      
      if (response.data.success) {
        setVersions(response.data.versions || []);
        // Find active version
        const active = response.data.versions?.find(v => v.is_active);
        setActiveVersion(active || null);
      }
    } catch (err) {
      console.error('[ConsentVersionCatalog] Error fetching versions:', err);
      setError('Failed to load consent versions');
    } finally {
      setLoading(false);
    }
  }, [funnelId]);

  useEffect(() => {
    fetchVersions();
  }, [fetchVersions]);

  // View version details
  const handleViewDetails = (version) => {
    setSelectedVersion(version);
    setShowDetailModal(true);
  };

  if (loading) {
    return (
      <Card className="bg-slate-900/50 border-slate-800">
        <CardContent className="py-8">
          <div className="flex items-center justify-center gap-2 text-slate-400">
            <Loader2 className="h-5 w-5 animate-spin" />
            Loading consent versions...
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="bg-slate-900/50 border-slate-800">
        <CardContent className="py-6">
          <div className="text-center">
            <AlertCircle className="h-6 w-6 text-red-400 mx-auto mb-2" />
            <p className="text-slate-300">{error}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchVersions}
              className="mt-3"
            >
              <RefreshCw className="h-4 w-4 mr-1" />
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Active Version Card */}
      <div>
        <Label className="text-xs text-slate-500 uppercase tracking-wider mb-2 block">
          Active Consent Version
        </Label>
        <ActiveVersionCard version={activeVersion} onViewDetails={handleViewDetails} />
      </div>
      
      {/* Version Catalog */}
      {versions.length > 0 && (
        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium text-slate-300 flex items-center gap-2">
                <Archive className="h-4 w-4 text-slate-500" />
                Version History
              </CardTitle>
              <Badge variant="outline" className="text-xs text-slate-400 border-slate-700">
                {versions.length} version{versions.length !== 1 ? 's' : ''}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <VersionCatalogTable versions={versions} onViewDetails={handleViewDetails} />
          </CardContent>
        </Card>
      )}
      
      {/* Version Detail Modal */}
      <VersionDetailModal
        version={selectedVersion}
        open={showDetailModal}
        onClose={() => setShowDetailModal(false)}
      />
    </div>
  );
}
